import { SampleFile } from "projen";
import { merge } from "ts-deepmerge";
import { resolveAstroProjectOutdir } from "./resolve-outdir";
import {
  TestRunner,
  TypeScriptProject,
  TypeScriptProjectOptions,
} from "./typescript-project";
import { AstroConfig } from "../astro/astro-config";
import {
  AstroConfigOptions,
  AstroIntegrationSpec,
} from "../astro/astro-config-options";
import { TurboRepo } from "../turbo";
import { VERSION } from "../versions";

/**
 * Configuration options for AstroProject.
 */
export interface AstroProjectOptions
  extends TypeScriptProjectOptions, AstroConfigOptions {
  /**
   * Astro package version.
   *
   * @default VERSION.ASTRO_VERSION
   */
  readonly astroVersion?: string;

  /**
   * Emit sample Astro starter files (`src/pages/index.astro`, `public/favicon.svg`).
   *
   * @default false
   */
  readonly sampleCode?: boolean;

  /**
   * Astro tsconfig preset to extend.
   *
   * @default "astro/tsconfigs/strict"
   */
  readonly astroTsconfigExtends?: string;
}

/**
 * Minimal Astro site scaffolded on top of {@link TypeScriptProject}.
 *
 * Replaces the `tsc` compile step with `astro check && astro build`, writes
 * `astro.config.mjs`, sets `"type": "module"`, and wires dev/preview tasks.
 */
export class AstroProject extends TypeScriptProject {
  /**
   * Rendered Astro config component.
   */
  public readonly astroConfig: AstroConfig;

  constructor(userOptions: AstroProjectOptions) {
    /*************************************************************************
     *
     * Resolve outdir
     *
     * When the caller omits `outdir`, derive it from the scoped package
     * name (`packageName ?? name`) as `sites/<scope>/<name>`. This enforces
     * the monorepo-layout contract (ADR-006) without requiring every
     * Astro sub-project to restate its own path. Resolution runs before
     * `super()` so the derived value is passed explicitly — otherwise the
     * TypeScriptProject base class would see `outdir: undefined` and place
     * the project directly under the monorepo root.
     *
     ************************************************************************/

    const resolvedOutdir =
      userOptions.outdir ??
      resolveAstroProjectOutdir(userOptions.packageName ?? userOptions.name);

    /**
     * Default to Vitest — Astro's native test runner — exclude `astro`
     * from projen's per-package ncu upgrade workflow so only the
     * configulator VERSION-driven update path owns it, and default
     * `tsconfig.compilerOptions.declaration` to `false` so `astro check`
     * (which runs `tsc --noEmit`) doesn't trip TS2742 on re-exported
     * third-party types under pnpm's hoisted node_modules layout.
     *
     * Use `ts-deepmerge`'s `merge()` (not a shallow spread) so a default
     * nested object like
     * `tsconfig: { compilerOptions: { declaration: false } }` survives a
     * user override that only sets unrelated tsconfig fields. `merge()`
     * also concatenates arrays, which gives the right semantics for
     * `depsUpgradeOptions.exclude`.
     */
    const defaultOptions: Partial<AstroProjectOptions> = {
      testRunner: TestRunner.VITEST,
      // Astro sites don't expose a library API surface — skip the
      // `@microsoft/api-extractor` wiring the TypeScriptProject base adds
      // by default. Callers can opt in with `apiExtractor: { ... }` if a
      // given site genuinely ships typed re-exports.
      apiExtractor: false,
      tsconfig: {
        compilerOptions: {
          declaration: false,
        },
      },
      depsUpgradeOptions: {
        exclude: ["astro"],
      },
    };
    const merged = merge(defaultOptions, userOptions) as AstroProjectOptions;
    const options: AstroProjectOptions = {
      ...merged,
      outdir: resolvedOutdir,
    };

    super(options);

    const astroVersion = options.astroVersion ?? VERSION.ASTRO_VERSION;
    const astroTsconfigExtends =
      options.astroTsconfigExtends ?? "astro/tsconfigs/strict";

    /**
     * Astro ships as ESM; the site package must be a module.
     */
    this.package.addField("type", "module");

    /**
     * Extend Astro's tsconfig preset so `astro check` and IDE tooling
     * understand `.astro` files, JSX, and bundler-style resolution.
     */
    this.tsconfig?.file.addOverride("extends", astroTsconfigExtends);
    this.tsconfig?.addInclude(".astro/types.d.ts");
    this.tsconfig?.addInclude("src/**/*.astro");

    /**
     * Replace projen's tsc-driven compile with Astro's build pipeline.
     */
    this.compileTask.reset();
    this.compileTask.exec("astro check");
    this.compileTask.exec("astro build");

    /**
     * Point `watch` at Astro's dev server (projen's default runs `tsc -w`,
     * which is meaningless for an Astro site).
     */
    this.tasks.tryFind("watch")?.reset("astro dev");

    /**
     * A website is not an npm tarball — reset `package` to a no-op so the
     * inherited `build` task does not emit `dist/js/*.tgz` alongside the
     * actual site output.
     */
    this.packageTask.reset();

    /**
     * Astro CLI tasks. `build` already exists on projen; add only the
     * Astro-specific lifecycle tasks.
     */
    if (!this.tasks.tryFind("dev")) {
      this.addTask("dev", {
        description: "Start the Astro dev server",
        exec: "astro dev",
        receiveArgs: true,
      });
    }
    if (!this.tasks.tryFind("preview")) {
      this.addTask("preview", {
        description: "Preview the built site locally",
        exec: "astro preview",
        receiveArgs: true,
      });
    }
    if (!this.tasks.tryFind("check")) {
      this.addTask("check", {
        description: "Run Astro's type and content checks",
        exec: "astro check",
      });
    }

    /**
     * Runtime and tooling dependencies.
     */
    this.addDeps(`astro@${astroVersion}`);
    this.addDevDeps(
      "@astrojs/check",
      "astro-eslint-parser",
      "eslint-plugin-astro",
      "prettier-plugin-astro",
    );

    /**
     * Register the Astro prettier plugin so `.astro` files are formatted.
     */
    const prettierConfig = this.tryFindObjectFile(".prettierrc.json");
    prettierConfig?.addOverride("plugins", ["prettier-plugin-astro"]);
    prettierConfig?.addOverride("overrides", [
      { files: "*.astro", options: { parser: "astro" } },
    ]);

    /**
     * Wire eslint-plugin-astro so `.astro` files are actually linted.
     * Projen's default `fileExtensions` is constructor-only and set to `.ts`,
     * so we add an explicit lint pattern to force `.astro` paths onto the
     * generated eslint command line.
     */
    this.eslint?.addPlugins("astro");
    this.eslint?.addExtends("plugin:astro/recommended");
    this.eslint?.addOverride({
      files: ["*.astro"],
      parser: "astro-eslint-parser",
    });
    this.eslint?.addLintPattern("src/**/*.astro");

    /**
     * Astro exposes its API surface via virtual modules (`astro:content`,
     * `astro:assets`, `astro:env`, etc.). `import/no-unresolved` cannot
     * resolve them, so ignore the `astro:*` namespace.
     */
    this.eslint?.addRules({
      "import/no-unresolved": ["error", { ignore: ["^astro:"] }],
    });

    /**
     * Astro-specific gitignore entries. `dist` is already handled by the
     * base TypeScriptProject.
     */
    this.gitignore.addPatterns(".astro", ".vercel", ".netlify");

    /**
     * Extend turbo inputs so the compile task invalidates on Astro-specific
     * source paths. Outputs (`dist/**`) are already added by the base class.
     */
    const turbo = TurboRepo.of(this);
    if (turbo?.compileTask) {
      turbo.compileTask.inputs.push("public/**", "astro.config.mjs");
    }

    /**
     * Render astro.config.mjs.
     */
    const integrations: Array<AstroIntegrationSpec> =
      options.integrations ?? [];
    this.astroConfig = new AstroConfig(this, {
      site: options.site,
      base: options.base,
      output: options.output,
      integrations,
      adapter: options.adapter,
    });

    /**
     * Optional starter files.
     */
    if (options.sampleCode === true) {
      new SampleFile(this, "src/pages/index.astro", {
        contents: DEFAULT_INDEX_ASTRO,
      });
      new SampleFile(this, "public/favicon.svg", {
        contents: DEFAULT_FAVICON_SVG,
      });
    }
  }
}

const DEFAULT_INDEX_ASTRO = `---
// Welcome to Astro!
---
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
    <meta name="viewport" content="width=device-width" />
    <title>Astro</title>
  </head>
  <body>
    <h1>Hello, Astro!</h1>
  </body>
</html>
`;

const DEFAULT_FAVICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128"><path d="M64 8a56 56 0 1 0 0 112A56 56 0 0 0 64 8Z" fill="#ff5d01"/></svg>
`;
